// @flow

import React from 'react';
import * as Yup from 'yup';
import { i18n, Analytics } from 'shared/utils';
import * as Actions from 'main-app/store/Actions';
import AddItemChildMutation from 'main-app/mutations/AddItemChild';
import UpdateItemChildMutation from 'main-app/mutations/UpdateItemChild';
import Button from 'shared/components/common/Button';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'shared/components/modal';
import {
  Formik,
  Form,
  SelectItemField,
  FieldGroup,
  FieldLabel,
} from 'shared/components/form';
import { BomTypeSelect } from 'shared/components/common/BOMTree';

type Props = {
  onClose: () => void,
  parentItemId: string,
  excludeIds: string[],
  childItem: ?Object,
};

const BOM_TYPE_OPTIONS = [
  { value: 'engineering', label: 'Engineering' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'configurable', label: 'Configurable' },
  { value: 'sales', label: 'Sales' },
  { value: 'service', label: 'Service' },
];

const AddUpdateItemChildModal = ({
  onClose,
  parentItemId,
  excludeIds,
  childItem,
}: Props) => (
  <Modal maxWidth={700}>
    <Formik
      initialValues={{
        childItem: childItem || null,
        usageQty: childItem?.usageQty || '',
        bomType: childItem?.bomType || 'manufacturing',
        referenceDesignator: childItem?.referenceDesignator || '',
        findNumber: childItem?.findNumber || '',
        notes: childItem?.notes || '',
        isPhantom: childItem?.isPhantom || false,
        scrapFactor: childItem?.scrapFactor || '',
        leadTimeOffsetDays: childItem?.leadTimeOffsetDays || '',
        isBackflush: childItem?.isBackflush || false,
        fixedQuantity: childItem?.fixedQuantity || '',
        isOptional: childItem?.isOptional || false,
        selectionGroup: childItem?.selectionGroup || '',
        isDefaultSelection: childItem?.isDefaultSelection || false,
        optionLabel: childItem?.optionLabel || '',
        minQuantity: childItem?.minQuantity || '',
        maxQuantity: childItem?.maxQuantity || '',
        priceAdjustment: childItem?.priceAdjustment || '',
        isCustomerVisible: childItem?.isCustomerVisible !== undefined ? childItem?.isCustomerVisible : true,
        salesDisplayName: childItem?.salesDisplayName || '',
        componentPrice: childItem?.componentPrice || '',
        discountPercent: childItem?.discountPercent || '',
        sortOrder: childItem?.sortOrder || '',
        isShippedSeparately: childItem?.isShippedSeparately || false,
        isReplaceable: childItem?.isReplaceable !== undefined ? childItem?.isReplaceable : true,
        serviceIntervalDays: childItem?.serviceIntervalDays || '',
        meanTimeBetweenFailure: childItem?.meanTimeBetweenFailure || '',
        repairMethod: childItem?.repairMethod || '',
        serviceNotes: childItem?.serviceNotes || '',
        isCriticalSpare: childItem?.isCriticalSpare || false,
      }}
      validationSchema={Yup.object().shape({
        childItem: Yup.object()
          .nullable()
          .required('Required'),
        usageQty: Yup.number(),
        bomType: Yup.string().required('Required'),
        referenceDesignator: Yup.string(),
        findNumber: Yup.string(),
        notes: Yup.string(),
        isPhantom: Yup.boolean(),
        scrapFactor: Yup.number().min(0).max(100),
        leadTimeOffsetDays: Yup.number().min(0).integer(),
        isBackflush: Yup.boolean(),
        fixedQuantity: Yup.number().min(0),
        isOptional: Yup.boolean(),
        selectionGroup: Yup.string(),
        isDefaultSelection: Yup.boolean(),
        optionLabel: Yup.string(),
        minQuantity: Yup.number().min(0),
        maxQuantity: Yup.number().min(0),
        priceAdjustment: Yup.number(),
        isCustomerVisible: Yup.boolean(),
        salesDisplayName: Yup.string(),
        componentPrice: Yup.number().min(0),
        discountPercent: Yup.number().min(0).max(100),
        sortOrder: Yup.number().min(0).integer(),
        isShippedSeparately: Yup.boolean(),
        isReplaceable: Yup.boolean(),
        serviceIntervalDays: Yup.number().min(0).integer(),
        meanTimeBetweenFailure: Yup.number().min(0).integer(),
        repairMethod: Yup.string(),
        serviceNotes: Yup.string(),
        isCriticalSpare: Yup.boolean(),
      })}
      onSubmit={async (values: *, { setSubmitting }: *) => {
        setSubmitting(true);

        const {
          usageQty,
          bomType,
          referenceDesignator,
          findNumber,
          notes,
          isPhantom,
          scrapFactor,
          leadTimeOffsetDays,
          isBackflush,
          fixedQuantity,
          isOptional,
          selectionGroup,
          isDefaultSelection,
          optionLabel,
          minQuantity,
          maxQuantity,
          priceAdjustment,
          isCustomerVisible,
          salesDisplayName,
          componentPrice,
          discountPercent,
          sortOrder,
          isShippedSeparately,
          isReplaceable,
          serviceIntervalDays,
          meanTimeBetweenFailure,
          repairMethod,
          serviceNotes,
          isCriticalSpare,
        } = values;

        const mutation = childItem
          ? UpdateItemChildMutation
          : AddItemChildMutation;

        try {
          await mutation.commit({
            variables: {
              input: {
                parentItemId,
                usageQty: parseFloat(usageQty),
                childItemId: childItem ? childItem.id : values?.childItem.value,
                bomType,
                referenceDesignator: referenceDesignator || null,
                findNumber: findNumber || null,
                notes: notes || null,
                isPhantom,
                scrapFactor: scrapFactor ? parseFloat(scrapFactor) : 0,
                leadTimeOffsetDays: leadTimeOffsetDays ? parseInt(leadTimeOffsetDays, 10) : 0,
                isBackflush,
                fixedQuantity: fixedQuantity ? parseFloat(fixedQuantity) : 0,
                isOptional,
                selectionGroup: selectionGroup || null,
                isDefaultSelection,
                optionLabel: optionLabel || null,
                minQuantity: minQuantity ? parseFloat(minQuantity) : null,
                maxQuantity: maxQuantity ? parseFloat(maxQuantity) : null,
                priceAdjustment: priceAdjustment ? parseFloat(priceAdjustment) : null,
                isCustomerVisible,
                salesDisplayName: salesDisplayName || null,
                componentPrice: componentPrice ? parseFloat(componentPrice) : 0,
                discountPercent: discountPercent ? parseFloat(discountPercent) : 0,
                sortOrder: sortOrder ? parseInt(sortOrder, 10) : 0,
                isShippedSeparately,
                isReplaceable,
                serviceIntervalDays: serviceIntervalDays ? parseInt(serviceIntervalDays, 10) : null,
                meanTimeBetweenFailure: meanTimeBetweenFailure ? parseInt(meanTimeBetweenFailure, 10) : null,
                repairMethod: repairMethod || null,
                serviceNotes: serviceNotes || null,
                isCriticalSpare,
              },
            },
          });

          onClose();
          Analytics.trackEvent(`${childItem ? 'Update' : 'Add'} Component`, {
            subComponentName: childItem
              ? childItem.name
              : values?.childItem?.data?.name,
          });
          Actions.alertNotification(
            i18n.t(`Component Successfully ${childItem ? 'Updated' : 'Added'}`),
            'success',
          );
        } catch (e) {
          setSubmitting(false);

          Actions.alertNotification(e.message, 'error');
        }
      }}
      render={({
        values,
        errors,
        isValid,
        isSubmitting,
        handleSubmit,
        setFieldValue,
        handleChange,
      }) => (
        <Form>
          <ModalHeader
            header={i18n.t(childItem ? 'Edit Component' : 'Add Component')}
            onClose={onClose}
          />
          <ModalBody withPadding>
            {childItem ? (
              <FieldGroup
                label={i18n.t('Item Name')}
                name="childItem"
                error={errors.childItem}
                value={childItem.name}
                disabled
              />
            ) : (
              <SelectItemField
                name="childItem"
                label={i18n.t('Search For Item')}
                placeholder={i18n.t('Select Item...')}
                error={errors.childItem}
                excludeIds={[parentItemId, ...excludeIds]}
              />
            )}
            <FieldGroup
              label={i18n.t('Estimated Usage')}
              name="usageQty"
              type="number"
              placeholder={i18n.t('Enter the estimated usage')}
              extendedLabel={
                childItem
                  ? childItem?.quantityUOM?.symbol
                  : values?.childItem?.data?.quantityUOM?.symbol
              }
              error={errors.usageQty}
            />
            <div style={{ marginBottom: '16px' }}>
              <FieldLabel>{i18n.t('BOM Type')}</FieldLabel>
              <BomTypeSelect
                name="bomType"
                value={values.bomType}
                onChange={handleChange}
              >
                {BOM_TYPE_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {i18n.t(option.label)}
                  </option>
                ))}
              </BomTypeSelect>
            </div>
            {values.bomType === 'engineering' && (
              <>
                <FieldGroup
                  label={i18n.t('Reference Designator')}
                  name="referenceDesignator"
                  placeholder={i18n.t('e.g., R1, C1, U2')}
                  error={errors.referenceDesignator}
                />
                <FieldGroup
                  label={i18n.t('Find Number')}
                  name="findNumber"
                  placeholder={i18n.t('Engineering find number')}
                  error={errors.findNumber}
                />
                <FieldGroup
                  label={i18n.t('Notes')}
                  name="notes"
                  type="textarea"
                  placeholder={i18n.t('Engineering notes/specifications')}
                  error={errors.notes}
                />
                <div style={{ marginBottom: '16px' }}>
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="checkbox"
                      name="isPhantom"
                      checked={values.isPhantom}
                      onChange={handleChange}
                      style={{ marginRight: '8px' }}
                    />
                    <span>{i18n.t('Phantom Assembly')}</span>
                    <span
                      style={{
                        marginLeft: '8px',
                        color: '#888',
                        fontSize: '12px',
                      }}
                    >
                      {i18n.t(
                        '(Not physically built, used for engineering grouping)',
                      )}
                    </span>
                  </label>
                </div>
              </>
            )}
            {values.bomType === 'manufacturing' && (
              <>
                <FieldGroup
                  label={i18n.t('Scrap Factor (%)')}
                  name="scrapFactor"
                  type="number"
                  placeholder={i18n.t('e.g., 5 for 5% waste')}
                  error={errors.scrapFactor}
                />
                <FieldGroup
                  label={i18n.t('Lead Time Offset (Days)')}
                  name="leadTimeOffsetDays"
                  type="number"
                  placeholder={i18n.t('Days before component is needed')}
                  error={errors.leadTimeOffsetDays}
                />
                <FieldGroup
                  label={i18n.t('Fixed Quantity')}
                  name="fixedQuantity"
                  type="number"
                  placeholder={i18n.t('Fixed qty per batch (e.g., setup material)')}
                  extendedLabel={
                    childItem
                      ? childItem?.quantityUOM?.symbol
                      : values?.childItem?.data?.quantityUOM?.symbol
                  }
                  error={errors.fixedQuantity}
                />
                <div style={{ marginBottom: '16px' }}>
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="checkbox"
                      name="isBackflush"
                      checked={values.isBackflush}
                      onChange={handleChange}
                      style={{ marginRight: '8px' }}
                    />
                    <span>{i18n.t('Backflush')}</span>
                    <span
                      style={{
                        marginLeft: '8px',
                        color: '#888',
                        fontSize: '12px',
                      }}
                    >
                      {i18n.t(
                        '(Auto-deduct from inventory when production is reported)',
                      )}
                    </span>
                  </label>
                </div>
              </>
            )}
            {values.bomType === 'configurable' && (
              <>
                <FieldGroup
                  label={i18n.t('Option Label')}
                  name="optionLabel"
                  placeholder={i18n.t('e.g., Color, Size, Material')}
                  error={errors.optionLabel}
                />
                <FieldGroup
                  label={i18n.t('Selection Group')}
                  name="selectionGroup"
                  placeholder={i18n.t('Group name for mutually exclusive options')}
                  error={errors.selectionGroup}
                />
                <FieldGroup
                  label={i18n.t('Min Quantity')}
                  name="minQuantity"
                  type="number"
                  placeholder={i18n.t('Minimum selectable quantity')}
                  error={errors.minQuantity}
                />
                <FieldGroup
                  label={i18n.t('Max Quantity')}
                  name="maxQuantity"
                  type="number"
                  placeholder={i18n.t('Maximum selectable quantity')}
                  error={errors.maxQuantity}
                />
                <FieldGroup
                  label={i18n.t('Price Adjustment')}
                  name="priceAdjustment"
                  type="number"
                  placeholder={i18n.t('Price change when this option is selected')}
                  error={errors.priceAdjustment}
                />
                <div style={{ marginBottom: '16px' }}>
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="checkbox"
                      name="isOptional"
                      checked={values.isOptional}
                      onChange={handleChange}
                      style={{ marginRight: '8px' }}
                    />
                    <span>{i18n.t('Optional Component')}</span>
                    <span
                      style={{
                        marginLeft: '8px',
                        color: '#888',
                        fontSize: '12px',
                      }}
                    >
                      {i18n.t(
                        '(Customer can choose to include or exclude this)',
                      )}
                    </span>
                  </label>
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="checkbox"
                      name="isDefaultSelection"
                      checked={values.isDefaultSelection}
                      onChange={handleChange}
                      style={{ marginRight: '8px' }}
                    />
                    <span>{i18n.t('Default Selection')}</span>
                    <span
                      style={{
                        marginLeft: '8px',
                        color: '#888',
                        fontSize: '12px',
                      }}
                    >
                      {i18n.t(
                        '(Pre-selected by default in this selection group)',
                      )}
                    </span>
                  </label>
                </div>
              </>
            )}
            {values.bomType === 'sales' && (
              <>
                <FieldGroup
                  label={i18n.t('Sales Display Name')}
                  name="salesDisplayName"
                  placeholder={i18n.t('Customer-friendly name (optional)')}
                  error={errors.salesDisplayName}
                />
                <FieldGroup
                  label={i18n.t('Component Price ($)')}
                  name="componentPrice"
                  type="number"
                  placeholder={i18n.t('Price within the kit/bundle')}
                  error={errors.componentPrice}
                />
                <FieldGroup
                  label={i18n.t('Bundle Discount (%)')}
                  name="discountPercent"
                  type="number"
                  placeholder={i18n.t('Discount vs. standalone price')}
                  error={errors.discountPercent}
                />
                <FieldGroup
                  label={i18n.t('Sort Order')}
                  name="sortOrder"
                  type="number"
                  placeholder={i18n.t('Display order on customer documents')}
                  error={errors.sortOrder}
                />
                <div style={{ marginBottom: '16px' }}>
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="checkbox"
                      name="isCustomerVisible"
                      checked={values.isCustomerVisible}
                      onChange={handleChange}
                      style={{ marginRight: '8px' }}
                    />
                    <span>{i18n.t('Customer Visible')}</span>
                    <span
                      style={{
                        marginLeft: '8px',
                        color: '#888',
                        fontSize: '12px',
                      }}
                    >
                      {i18n.t(
                        '(Show on quotes, packing slips, and invoices)',
                      )}
                    </span>
                  </label>
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="checkbox"
                      name="isShippedSeparately"
                      checked={values.isShippedSeparately}
                      onChange={handleChange}
                      style={{ marginRight: '8px' }}
                    />
                    <span>{i18n.t('Ships Separately')}</span>
                    <span
                      style={{
                        marginLeft: '8px',
                        color: '#888',
                        fontSize: '12px',
                      }}
                    >
                      {i18n.t(
                        '(Ships in a separate package from the main kit)',
                      )}
                    </span>
                  </label>
                </div>
              </>
            )}
            {values.bomType === 'service' && (
              <>
                <FieldGroup
                  label={i18n.t('Repair Method')}
                  name="repairMethod"
                  placeholder={i18n.t('e.g., Replace, Repair, Recalibrate, Overhaul')}
                  error={errors.repairMethod}
                />
                <FieldGroup
                  label={i18n.t('Service Interval (Days)')}
                  name="serviceIntervalDays"
                  type="number"
                  placeholder={i18n.t('Recommended maintenance interval')}
                  error={errors.serviceIntervalDays}
                />
                <FieldGroup
                  label={i18n.t('MTBF (Hours)')}
                  name="meanTimeBetweenFailure"
                  type="number"
                  placeholder={i18n.t('Mean Time Between Failure')}
                  error={errors.meanTimeBetweenFailure}
                />
                <FieldGroup
                  label={i18n.t('Service Notes')}
                  name="serviceNotes"
                  type="textarea"
                  placeholder={i18n.t('Field service instructions and notes')}
                  error={errors.serviceNotes}
                />
                <div style={{ marginBottom: '16px' }}>
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="checkbox"
                      name="isReplaceable"
                      checked={values.isReplaceable}
                      onChange={handleChange}
                      style={{ marginRight: '8px' }}
                    />
                    <span>{i18n.t('Field Replaceable')}</span>
                    <span
                      style={{
                        marginLeft: '8px',
                        color: '#888',
                        fontSize: '12px',
                      }}
                    >
                      {i18n.t(
                        '(Can be replaced on-site by service technicians)',
                      )}
                    </span>
                  </label>
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="checkbox"
                      name="isCriticalSpare"
                      checked={values.isCriticalSpare}
                      onChange={handleChange}
                      style={{ marginRight: '8px' }}
                    />
                    <span>{i18n.t('Critical Spare')}</span>
                    <span
                      style={{
                        marginLeft: '8px',
                        color: '#c62828',
                        fontSize: '12px',
                      }}
                    >
                      {i18n.t(
                        '(Must always be kept in stock for emergencies)',
                      )}
                    </span>
                  </label>
                </div>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              type="submit"
              theme="blue"
              disabled={!isValid}
              loading={isSubmitting}
              onClick={handleSubmit}
            >
              {i18n.t('Add Component')}
            </Button>
          </ModalFooter>
        </Form>
      )}
    />
  </Modal>
);

export default AddUpdateItemChildModal;
